import { useState } from "react";
import { useIntl, FormattedMessage } from "react-intl";
import { MenuItem, Tooltip } from "react-bootstrap";
import { useNavigate } from "react-router-dom-v5-compat";
import { useQueryClient } from "@tanstack/react-query";
import ItemMenu from "../ItemMenu";
import { useApi, useSubscriptionQuery } from "../../hooks";
import ConfirmationModal from "../ConfirmationModal";
import { FormattedNumber } from "../Format";
import AllObjectsSelectModal from "../AllObjects/AllObjectsSelectModal";
import { withRules, rulesShape, useNotifier } from "../../context";
import {
  assetShape,
  accessRightsShape,
  nodeShape,
  instrumentationShape,
} from "../../shapes";

import { formatAssetDetails, url } from "../../utils";

import { BadRequestError, ConflictError } from "../../api";
import DocumentExportModal from "../Documents/DocumentExportModal";
import Picture from "../Pictures/Picture";
import ListItem from "../List/ListItem";
import CustomOverlayTrigger from "../CustomOverlayTrigger";
import {
  getAssignedNodesChips,
  getSpecificationChips,
} from "../../utils/chipsUtils";

type OwnProps = {
  asset?: assetShape;
  parentAccessRights?: accessRightsShape;
  onAssetRemoved?: $TSFixMeFunction;
  showItemMenu?: boolean;
  rules?: rulesShape;
  parentInstrumentation?: instrumentationShape;
  parentNode?: nodeShape;
  canMove?: boolean;
  options?: string[];
};

const defaultProps: Partial<OwnProps> = {
  asset: undefined,
  parentNode: undefined,
  parentInstrumentation: undefined,
  showItemMenu: false,
  parentAccessRights: undefined,
  onAssetRemoved: undefined,
  canMove: true,
};

type Props = OwnProps & typeof defaultProps;

export function AssetItem({
  asset,
  onAssetRemoved,
  showItemMenu,
  parentNode,
  parentInstrumentation,
  parentAccessRights,
  rules,
  canMove,
  options,
}: Props) {
  const api = useApi();
  const navigate = useNavigate();
  const notifier = useNotifier();
  const intl = useIntl();
  const { data: subscription } = useSubscriptionQuery();
  const queryClient = useQueryClient();

  const t = (translationId: $TSFixMe) =>
    intl.formatMessage({ id: translationId });

  const defaultPictureSquareSize = 50;

  const [assigning, setAssigning] = useState(false);
  const [moving, setMoving] = useState(false);
  const [unassigning, setUnassigning] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [manualAssignment, setManualAssignment] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const resetActionsState = () => {
    setAssigning(false);
    setUnassigning(false);
    setDeleting(false);
    setMoving(false);
  };

  const handleApiErrors = (error: $TSFixMe) => {
    if (
      error instanceof BadRequestError &&
      error.errors.length > 0 &&
      error.errors[0].type === "associations_already_added"
    ) {
      notifier.showError(t`asset.actions.assign.error`);
    } else {
      notifier.showError(t`api.error.unknown`);
    }
  };

  const { data: assetAccessRights, isFetching: isLoadingAccessRights } =
    api.get.useQuery(
      "/users/current/access_rights",
      {
        permitable_type: "Asset",
        permitable_id: (asset as $TSFixMe)?.id,
      },
      {
        initialData: {
          canUpdate: false,
          canDelete: false,
        },
        select: (response: $TSFixMe) => ({
          type: "Asset",
          id: (asset as $TSFixMe)?.id,
          canRead: response.can_read,
          canUpdate: response.can_update,
          canDelete: response.can_delete,
          canPermit: response.can_permit,
        }),
        enabled: showItemMenu && isMenuOpen,
      },
    );

  const { mutate: assignNodeToAsset } = api.post.useMutation(
    `/assets/${(asset as $TSFixMe)?.id}/nodes`,
    {
      onSuccess: () => {
        if (!parentNode && !parentInstrumentation) {
          notifier.showSuccess(t`asset.actions.move_assignment.notification`);
          // @ts-expect-error TS(2722) FIXME: Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
          onAssetRemoved(asset);
        } else if (assigning) {
          notifier.showSuccess(t`asset.actions.assign.notification`);
        }
        resetActionsState();
      },
      onError: (error: $TSFixMe) => {
        handleApiErrors(error);
      },
    },
  );

  const { mutate: assignInstrumentationToAsset } = api.post.useMutation(
    `/assets/${(asset as $TSFixMe)?.id}/instrumentations`,
    {
      onError: (error: $TSFixMe) => {
        handleApiErrors(error);
      },
    },
  );

  const { mutate: assignAssetToSubscription } = api.post.useMutation(
    `/subscriptions/${subscription?.id}/assets`,
    {
      onSuccess: () => {
        setManualAssignment(true);
        notifier.showSuccess(
          intl.formatMessage({ id: "asset.actions.assign.notification" }),
        );
      },
    },
  );

  const { mutate: deleteAsset } = api.delete.useMutation(
    `/assets/${(asset as $TSFixMe)?.id}`,
    {
      onSuccess: async () => {
        notifier.showSuccess(t`asset.actions.delete.notification`);
        // @ts-expect-error TS(2722) FIXME: Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
        onAssetRemoved(asset);
        queryClient.invalidateQueries({ queryKey: ["/subscription"] });
      },
      onError: (error: $TSFixMe) => {
        if (
          error instanceof ConflictError &&
          error.errors.length > 0 &&
          error.errors[0].type === "assigned_restriction"
        ) {
          notifier.showError(t`api.error.asset.assigned_restriction`);
        } else if (
          error instanceof ConflictError &&
          error.errors.length > 0 &&
          error.errors[0].type === "device_not_deactivated_conflict"
        ) {
          notifier.showError(t`api.error.device_not_deactivated_conflict`);
        } else {
          notifier.showError(t`api.error.unknown`);
        }
      },
    },
  );

  const { mutate: unassignAssetFromNode } = api.delete.useMutation(
    `/nodes/${(parentNode as $TSFixMe)?.id}/assets`,
    {
      onSuccess: () => {
        notifier.showSuccess(t`asset.actions.unassign.notification`);
        // @ts-expect-error TS(2722) FIXME: Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
        onAssetRemoved(asset);
      },
    },
  );

  const { mutate: unassignAssetFromInstrumentation } = api.delete.useMutation(
    `/instrumentations/${(parentInstrumentation as $TSFixMe)?.id}/assets`,
    {
      onSuccess: () => {
        notifier.showSuccess(t`asset.actions.unassign.notification`);
        // @ts-expect-error TS(2722) FIXME: Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
        onAssetRemoved(asset);
      },
    },
  );

  const { mutate: unassignNodeFromAsset } = api.delete.useMutation(
    `/assets/${(asset as $TSFixMe)?.id}/nodes`,
    {
      onSuccess: () => {
        notifier.showSuccess(t`asset.actions.move_assignment.notification`);
        // @ts-expect-error TS(2722) FIXME: Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
        onAssetRemoved(asset);
        resetActionsState();
      },
    },
  );

  const handleOnConfirm = () => {
    resetActionsState();
    if (deleting) {
      // @ts-expect-error TS(2345) FIXME: Argument of type 'null' is not assignable to param... Remove this comment to see the full error message
      deleteAsset(null);
    }
    if (unassigning) {
      if (parentNode) {
        unassignAssetFromNode({ assets: [{ id: (asset as $TSFixMe)?.id }] });
      }
      if (parentInstrumentation) {
        unassignAssetFromInstrumentation({
          assets: [{ id: (asset as $TSFixMe)?.id }],
        });
      }
    }
  };

  const handleOnItemMenuOpen = () => {
    setIsMenuOpen(true);
  };
  const handleOnMoveAssignmentClick = () => {
    setMoving(true);
  };

  const handleOnAssignClick = () => {
    setAssigning(true);
  };

  const handleOnUnassignClick = () => {
    setUnassigning(true);
  };

  const handleOnAssignToSubscriptionClick = () => {
    if (
      // @ts-expect-error TS(2571) FIXME: Object is of type 'unknown'.
      subscription?.asset_quota > 0 &&
      // @ts-expect-error TS(2571) FIXME: Object is of type 'unknown'.
      subscription?.number_assigned_assets >= subscription?.asset_quota
    ) {
      notifier.showError(
        intl.formatMessage(
          { id: "subscription.limit.asset_details" },
          { asset_quota: subscription?.asset_quota },
        ),
      );
      return;
    }

    assignAssetToSubscription({ assets: [{ id: (asset as $TSFixMe)?.id }] });
  };

  const handleOnDeleteClick = () => {
    setDeleting(true);
  };

  const handleOnModalClose = () => {
    resetActionsState();
  };

  const handleOnNodeSelect = (node: $TSFixMe) => {
    if (assigning && node) {
      assignNodeToAsset({
        nodes: [{ id: node.id }],
      });
    }
    /* istanbul ignore else */
    if (moving && asset && parentNode && node) {
      assignNodeToAsset(
        { nodes: [{ id: node.id }] },
        {
          onSuccess: () => {
            unassignNodeFromAsset({
              nodes: [{ id: (parentNode as $TSFixMe).id }],
            });
          },
        },
      );
    }
  };

  const handleOnInstrumentationSelect = (instrumentation: $TSFixMe) => {
    if (assigning && instrumentation) {
      assignInstrumentationToAsset(
        { instrumentations: [{ id: instrumentation.id }] },
        {
          onSuccess: () => {
            if (!parentNode && !parentInstrumentation) {
              notifier.showSuccess(
                t`asset.actions.move_assignment.notification`,
              );
              // @ts-expect-error TS(2722) FIXME: Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
              onAssetRemoved(asset);
            } else {
              notifier.showSuccess(t`asset.actions.assign.notification`);
            }
            resetActionsState();
          },
        },
      );
    }
    /* istanbul ignore else */
    if (moving && asset && parentNode && instrumentation) {
      assignInstrumentationToAsset(
        { instrumentations: [{ id: instrumentation.id }] },
        {
          onSuccess: () => {
            unassignNodeFromAsset({
              nodes: [{ id: (parentNode as $TSFixMe).id }],
            });
          },
        },
      );
    }
  };

  const assetAssigned = manualAssignment || (asset as $TSFixMe)?.inSubscription;
  // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
  const assetRules = rules.asset(asset);
  // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
  const applicationRules = rules.application();

  const getListItemValue = () =>
    assetAssigned && (asset as $TSFixMe)?.value ? (
      <>
        <FormattedNumber
          minimumFractionDigits={assetRules.get(
            "minDecimalDigits(asset.value.key)",
          )}
          maximumFractionDigits={assetRules.get(
            "maxDecimalDigits(asset.value.key)",
          )}
          value={(asset as $TSFixMe)?.value.value}
        />
        <span>{` ${(asset as $TSFixMe)?.value.unit}`}</span>
      </>
    ) : null;

  const getUnassignedAssetItem = () => (
    <ItemMenu onOpen={handleOnItemMenuOpen}>
      <MenuItem onSelect={handleOnAssignToSubscriptionClick}>
        <FormattedMessage id="button.assign_to_subscription" />
      </MenuItem>
    </ItemMenu>
  );

  const getAssignedAssetItem = () => (
    <>
      {showItemMenu ? (
        <ItemMenu
          isLoading={isLoadingAccessRights}
          onOpen={handleOnItemMenuOpen}
        >
          <MenuItem
            onSelect={() =>
              navigate(url(`/assets/${(asset as $TSFixMe)?.id}/edit`))
            }
            // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
            disabled={!assetAccessRights.canUpdate}
          >
            <FormattedMessage id="button.edit" />
          </MenuItem>
          {parentNode && (
            <>
              <MenuItem
                onSelect={handleOnAssignClick}
                // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
                disabled={!assetAccessRights.canPermit}
              >
                <FormattedMessage id="button.assign" />
              </MenuItem>
              <MenuItem
                onSelect={handleOnUnassignClick}
                disabled={
                  // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
                  !parentAccessRights.canUpdate || !assetAccessRights.canPermit
                }
              >
                <FormattedMessage id="button.unassign" />
              </MenuItem>
              {canMove && (
                <MenuItem
                  onSelect={handleOnMoveAssignmentClick}
                  disabled={
                    // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
                    !parentAccessRights.canUpdate ||
                    // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
                    !assetAccessRights.canPermit
                  }
                >
                  <FormattedMessage id="button.move" />
                </MenuItem>
              )}
            </>
          )}
          {!parentNode && !parentInstrumentation && canMove && (
            <MenuItem
              onSelect={handleOnAssignClick}
              // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
              disabled={!assetAccessRights.canPermit}
            >
              <FormattedMessage id="button.move" />
            </MenuItem>
          )}
          {parentInstrumentation && (
            <MenuItem
              onSelect={handleOnUnassignClick}
              disabled={
                // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
                !parentAccessRights.canUpdate || !assetAccessRights.canPermit
              }
            >
              <FormattedMessage id="button.unassign" />
            </MenuItem>
          )}
          {applicationRules.get("showFilesExportInContextMenu") && (
            <DocumentExportModal object={{ asset }}>
              {/* @ts-expect-error TS(2322) FIXME: Type '({ showModal }: { showModal: any; }) => Elem... Remove this comment to see the full error message */}
              {({ showModal }) => (
                <MenuItem onSelect={showModal}>
                  <FormattedMessage id="button.export_files" />
                </MenuItem>
              )}
            </DocumentExportModal>
          )}
          <MenuItem
            onSelect={handleOnDeleteClick}
            // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
            disabled={!assetAccessRights.canDelete}
          >
            <FormattedMessage id="button.delete" />
          </MenuItem>
        </ItemMenu>
      ) : null}

      {assigning || moving ? (
        <AllObjectsSelectModal
          // @ts-expect-error TS(2322) FIXME: Type '{ onClose: () => void; onSelectNode: (node: ... Remove this comment to see the full error message
          onClose={handleOnModalClose}
          onSelectNode={handleOnNodeSelect}
          onSelectInstrumentation={handleOnInstrumentationSelect}
          canSelectInstrumentations
          canSelectSystems
          type={
            ((parentNode || parentInstrumentation) && assigning && "assign") ||
            "move"
          }
        />
      ) : null}

      {deleting || unassigning ? (
        <ConfirmationModal
          // @ts-expect-error TS(2322) FIXME: Type '{ intl: IntlShape; id: string; show: true; t... Remove this comment to see the full error message
          intl={intl}
          id="confirmation-modal"
          show
          titleText={intl.formatMessage({
            id: deleting
              ? "asset.actions.delete.modal_title"
              : "asset.actions.unassign.confirmation.title",
          })}
          messageText={intl.formatMessage({
            id: deleting
              ? "asset.actions.delete.modal_message"
              : "asset.actions.unassign.confirmation.message",
          })}
          onConfirm={handleOnConfirm}
          onClose={handleOnModalClose}
        />
      ) : null}
    </>
  );

  const specifications =
    assetAssigned && options?.includes("specifications")
      ? getSpecificationChips((asset as $TSFixMe)?.specifications, intl)
      : undefined;
  const nodes =
    assetAssigned && options?.includes("nodes")
      ? getAssignedNodesChips((asset as $TSFixMe)?.nodes, intl)
      : undefined;

  const getListItem = (listItemActions: $TSFixMe) => (
    <ListItem
      id={`asset-item-${(asset as $TSFixMe)?.id}`}
      data-testid={`asset-item-${(asset as $TSFixMe)?.id}`}
      image={
        (asset as $TSFixMe)?.thumbnailUrl ? (
          <Picture
            width={defaultPictureSquareSize}
            height={defaultPictureSquareSize}
            src={(asset as $TSFixMe)?.thumbnailUrl}
            placeholder="icon icon-eh-device"
          />
        ) : undefined
      }
      icon={(asset as $TSFixMe)?.thumbnailUrl ? undefined : "icon-eh-device"}
      title={(asset as $TSFixMe)?.serialNumber}
      description={formatAssetDetails(asset)}
      namur={assetAssigned ? (asset as $TSFixMe)?.assetStatusCode : null}
      target={url(`/assets/${(asset as $TSFixMe)?.id}`)}
      disabled={!assetAssigned}
      // @ts-expect-error TS(2554) FIXME: Expected 0 arguments, but got 1.
      value={getListItemValue(assetAssigned)}
      actions={listItemActions}
      specifications={specifications}
      nodes={nodes}
    />
  );
  const getDisplayTooltip = () => (
    <Tooltip id="tooltip">
      {intl.formatMessage({
        id: "subscription.asset_inactive_because_not_assigned_message",
      })}
    </Tooltip>
  );

  return assetAssigned ? (
    getListItem(getAssignedAssetItem())
  ) : (
    <CustomOverlayTrigger placement="bottom" overlay={getDisplayTooltip()}>
      <span>{getListItem(getUnassignedAssetItem())}</span>
    </CustomOverlayTrigger>
  );
}

AssetItem.defaultProps = defaultProps;

export default withRules(AssetItem);
